import { z } from 'zod';
import { AssetTypeSchema } from './domain.js';
import { CANDIDATES_PATH } from './experiment.js';
/**
 * The Composition bench: the narrow way a finished single-image Candidate is adjusted.
 *
 * Everything here is deliberately small. The pieces are what the Studio inferred the
 * asset to be made of, the moves are relative nudges, and there is no layer, mask, blend
 * mode, coordinate or prompt anywhere in the contract - not because the screen hides
 * them, but because they do not exist on this side of the seam either.
 */
export const PieceKindSchema = z.enum(['background', 'symbol', 'headline', 'mark', 'suppliedLogo']);
export const NudgeSchema = z.enum(['up', 'down', 'left', 'right', 'smaller', 'larger']);
export const CompositionPieceSchema = z.object({
    kind: PieceKindSchema,
    label: z.string().min(1),
    /** One line saying which part of this Direction the piece is. */
    note: z.string(),
});
/**
 * How this Asset Type is previewed. Both are previews and only previews: an icon is
 * shown under the Google masks and at small sizes, a feature graphic under
 * representative crops, and neither is baked into the exported bytes.
 */
export const CompositionPreviewSchema = z.enum(['iconMasks', 'featureGraphicCrops']);
export const CompositionResourceSchema = z.object({
    candidateId: z.string().min(1),
    experimentId: z.string().min(1),
    assetType: AssetTypeSchema,
    artifactId: z.string().nullable(),
    headline: z.string(),
    preview: CompositionPreviewSchema,
    pieces: z.array(CompositionPieceSchema),
});
export const PieceAdjustmentSchema = z.object({
    piece: PieceKindSchema,
    /** In the order they were made. A repeat is said in words, never counted into an offset. */
    nudges: z.array(NudgeSchema).min(1),
});
/**
 * `Create edited Candidate`. Until this arrives, a nudge is a local draft and nothing in
 * the Studio has changed; when it arrives it creates a descendant and the parent stays
 * exactly as it was.
 */
export const CompositionEditRequestSchema = z.object({
    adjustments: z.array(PieceAdjustmentSchema).optional(),
    instruction: z.string().optional(),
});
const PIECE_WORD = {
    background: 'фон',
    symbol: 'символ',
    headline: 'заголовок',
    mark: 'знак App',
    suppliedLogo: 'загруженный логотип',
};
/** The same pieces, said the way a provider prompt says them. */
const PIECE_WORD_EN = {
    background: 'the background',
    symbol: 'the symbol',
    headline: 'the headline',
    mark: 'the App mark',
    suppliedLogo: 'the supplied logo',
};
const MOVE_WORD = {
    up: 'вверх',
    down: 'вниз',
    left: 'влево',
    right: 'вправо',
};
const MOVE_WORD_EN = {
    up: 'up',
    down: 'down',
    left: 'left',
    right: 'right',
};
const SCALE_WORD = {
    smaller: 'меньше',
    larger: 'больше',
};
const SCALE_WORD_EN = {
    smaller: 'smaller',
    larger: 'larger',
};
function editFacts(adjustments) {
    return adjustments
        .filter((adjustment) => adjustment.nudges.length > 0)
        .map((adjustment) => {
        const counts = new Map();
        for (const nudge of adjustment.nudges)
            counts.set(nudge, (counts.get(nudge) ?? 0) + 1);
        const counted = (directions) => directions
            .filter((direction) => counts.has(direction))
            .map((direction) => ({ direction, times: counts.get(direction) ?? 0 }));
        return {
            piece: adjustment.piece,
            moves: counted(['up', 'down', 'left', 'right']),
            scales: counted(['smaller', 'larger']),
        };
    });
}
/** Repetition is said, never counted: there is no numeric panel to read a count off. */
function intensity(times, once) {
    if (times <= 1)
        return once;
    if (times === 2)
        return `ещё ${once}`;
    return `намного ${once}`;
}
function scaleWord(times, direction) {
    const word = SCALE_WORD[direction];
    if (times <= 1)
        return `сделан ${word}`;
    if (times === 2)
        return `сделан заметно ${word}`;
    return `сделан намного ${word}`;
}
/** The same repetition, said the way a provider prompt says it. */
function intensityEnglish(times, once) {
    if (times <= 1)
        return once;
    if (times === 2)
        return `${once} once more`;
    return `${once} much further`;
}
function scaleWordEnglish(times, direction) {
    const word = SCALE_WORD_EN[direction];
    if (times <= 1)
        return `made ${word}`;
    if (times === 2)
        return `made noticeably ${word}`;
    return `made much ${word}`;
}
/**
 * The draft in words - the same sentence on both sides of the seam.
 *
 * The operator reads it on the bench before deciding to keep the change, and the same
 * sentence is what a descendant Candidate is generated from. A nudge stays relative all
 * the way through: nothing here becomes an offset, a percentage or a coordinate,
 * because there is nowhere in the Studio where such a number would be true.
 */
export function describeAdjustments(adjustments) {
    return editFacts(adjustments)
        .map((fact) => {
        const moves = fact.moves.map((move) => intensity(move.times, MOVE_WORD[move.direction]));
        const scales = fact.scales.map((scale) => scaleWord(scale.times, scale.direction));
        const parts = [...(moves.length === 0 ? [] : [`сдвинут ${moves.join(' и ')}`]), ...scales];
        return `${PIECE_WORD[fact.piece]} ${parts.join(' и ')}`;
    })
        .join(', ');
}
/**
 * The same edit, in the language a provider prompt is written in.
 *
 * The Studio writes its part of a prompt in English, and the operator reads theirs in
 * Russian: one edit, one meaning, two readings of the same facts. The wording is
 * translated once, here, next to the Russian it comes from - never re-invented where
 * the prompt is assembled, and never left as Russian inside an English prompt.
 */
export function describeAdjustmentsInEnglish(adjustments) {
    return editFacts(adjustments)
        .map((fact) => {
        const moves = fact.moves.map((move) => `moved ${intensityEnglish(move.times, MOVE_WORD_EN[move.direction])}`);
        const scales = fact.scales.map((scale) => scaleWordEnglish(scale.times, scale.direction));
        const parts = [...moves, ...scales];
        return `${PIECE_WORD_EN[fact.piece]} is ${parts.join(' and ')}`;
    })
        .join(', ');
}
export function compositionPath(candidateId) {
    return `${CANDIDATES_PATH}/${candidateId}/composition`;
}
export function compositionEditPath(candidateId) {
    return `${CANDIDATES_PATH}/${candidateId}/composition-edit`;
}
//# sourceMappingURL=composition.js.map